// client\src\hooks\useTemplateAnamnese.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api'; 
import useAlert from './useAlert';

const ANAMNESE_TEMPLATES_QUERY_KEY = 'templatesAnamnese';
const BASE_ROUTE = 'templates/anamnese';

export const useTemplatesAnamnese = (params = {}, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [ANAMNESE_TEMPLATES_QUERY_KEY, params],
        queryFn: async () => {
            try {
                // ✅ CORREÇÃO FINAL: Voltamos a usar o apiClient (Axios),
                // que agora funcionará corretamente por causa do proxy.
                const { data } = await apiClient.get(`/${BASE_ROUTE}`, { params });
                // Acessa a propriedade 'data' da resposta da API.
                return data.data || []; 
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar templates de anamnese.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 15,
        ...options,
    });
};

export const useTemplateAnamneseById = (id, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [ANAMNESE_TEMPLATES_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}/${id}`);
                return data.data || data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar detalhes do template.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 15,
        ...options,
    });
};

export const useMutateTemplateAnamnese = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const onMutationSuccess = () => {
        queryClient.invalidateQueries({ queryKey: [ANAMNESE_TEMPLATES_QUERY_KEY] });
    };

    const addTemplateAnamnese = useMutation({
        mutationFn: (templateData) => apiClient.post(`/${BASE_ROUTE}`, templateData),
        onSuccess: () => {
            setAlert('Template de anamnese criado com sucesso!', 'success');
            onMutationSuccess();
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar template.', 'danger'),
    });

    const updateTemplateAnamnese = useMutation({
        mutationFn: ({ id, templateData }) => apiClient.put(`/${BASE_ROUTE}/${id}`, templateData),
        onSuccess: () => {
            setAlert('Template de anamnese atualizado com sucesso!', 'success');
            onMutationSuccess();
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar template.', 'danger'),
    });

    const deleteTemplateAnamnese = useMutation({
        mutationFn: (id) => apiClient.delete(`/${BASE_ROUTE}/${id}`),
        onSuccess: () => {
            setAlert('Template de anamnese removido com sucesso!', 'success');
            onMutationSuccess();
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover template.', 'danger'),
    });
    
    const importTemplateAnamnese = useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            const response = await apiClient.post(`/${BASE_ROUTE}/import`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data;
        },
        onSuccess: () => {
            setAlert('Template de anamnese importado com sucesso!', 'success');
            onMutationSuccess();
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao importar template.', 'danger'),
    });

    const exportTemplateAnamnese = async (id) => {
        try {
            const response = await apiClient.get(`/${BASE_ROUTE}/${id}/export`, { responseType: "blob" });
            const blob = new Blob([response.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `template-anamnese-${id}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setAlert('Template exportado com sucesso!', 'success');
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Erro ao exportar o template.';
            setAlert(errorMsg, 'danger');
        }
    };

    return { 
        addTemplateAnamnese, 
        updateTemplateAnamnese, 
        deleteTemplateAnamnese,
        importTemplateAnamnese,
        exportTemplateAnamnese,
        isPendingAnamnese: addTemplateAnamnese.isPending || updateTemplateAnamnese.isPending || deleteTemplateAnamnese.isPending || importTemplateAnamnese.isPending,
    };
};